/**
 * The pay stub's grouped lines and summary, as `GET /payroll/pay-periods/:id/
 * employees/:userId` returns them (api#1127).
 *
 * EVERY FIGURE HERE IS THE API'S. A group's amount, each summary line and the
 * total are read, never summed. This replaces `buildPayPeriodSummary` on the
 * timesheet detail screen: that helper partitioned the total client-side, and
 * once the server started moving ticket stat lines out of ticket earnings
 * (a description match only it can see) a client partition would have been a
 * second, disagreeing answer to the same question.
 *
 * Reads tolerate either casing. A figure that is missing or not a number is
 * `null` -- it renders `—`, never `$0.00`.
 */

import { fmtHours } from './display-labels';
import { formatCalendarDate, instantTenantDay } from './calendar-date';

export type PayCategory = 'ticket' | 'shop' | 'hourly_field' | 'stat_holiday';

export interface PayGroup {
  groupKey: string;
  familyKey: string | null;
  label: string | null;
  category: PayCategory | null;
  ticketId: string | null;
  ticketNumber: string | null;
  customerName: string | null;
  /** YYYY-MM-DD, the group's earliest entry. A calendar day, not an instant. */
  date: string | null;
  amount: number | null;
  entryIds: string[];
  /**
   * A clock-in group's job, and the ticket on that job for that day when one
   * exists. Both null on ticket-paid groups, which carry `ticketNumber`.
   */
  jobNumber: string | null;
  jobTicketNumber: string | null;
  /**
   * A clock group's (shop / hourly field) hours and rate, from the API's
   * `clockGroupFigures` (api#1193). `rate` is null with a `rateReason` when the
   * shifts disagree on a rate or one is unpriced -- never an average. All three
   * are null on a ticket group, which carries no such figures.
   */
  hours: number | null;
  rate: number | null;
  rateReason: ClockRateReason | null;
  /** The group's clock shifts, or null on a ticket group / an older API. */
  shifts: ClockShift[] | null;
}

export type ClockRateReason = 'mixed_rates' | 'unpriced';

export interface ClockShift {
  entryId: string;
  timesheetId: string | null;
  /** YYYY-MM-DD, the tenant's day the shift started. */
  date: string | null;
  /** ISO UTC instants. */
  clockInAt: string | null;
  clockOutAt: string | null;
  /** 'HH:mm' in the tenant's zone, formatted by the API. Carries no date. */
  clockInLocal: string | null;
  clockOutLocal: string | null;
  hours: number | null;
  rate: number | null;
  amount: number | null;
}

export interface PaySummaryLines {
  ticketEarnings: number | null;
  shopTime: number | null;
  hourlyFieldTime: number | null;
  statHoliday: number | null;
  bonuses: number | null;
  mileage: number | null;
  expenses: number | null;
  /**
   * The premium only — except for a US hourly worker whose overtime the API
   * split (api, fix/us-payroll-ot-lines): then it is the WHOLE overtime pay,
   * hours × 1.5 × rate, the straight time of those hours has left
   * shopTime / hourlyFieldTime, and the two keys below are set. Null otherwise.
   */
  overtime: number | null;
  /**
   * The Shop time / Hourly field time lines' hours and rate (api#1193). null
   * when there is no such time; the rate is also null when the shifts
   * disagree or one is unpriced.
   */
  shopHours: number | null;
  shopRate: number | null;
  hourlyFieldHours: number | null;
  hourlyFieldRate: number | null;
  overtimeHours: number | null;
  overtimeRate: number | null;
  other: number | null;
  total: number | null;
  /** 0 when the lines add up to the total. Anything else is an API defect. */
  residual: number | null;
}

export interface StubAdjustment {
  id: string;
  adjustment_type: string | null;
  description: string | null;
  amount: string | number | null;
  source_expense_id: string | null;
}

export interface PayStub {
  /** Raw entry rows, each carrying the API's classification fields. */
  entries: any[];
  /** `null` when the API sent no `groups` key at all -- an older deploy. */
  groups: PayGroup[] | null;
  lines: PaySummaryLines | null;
  adjustments: StubAdjustment[];
  /**
   * The API's own answer to "is this a US hourly worker" (`isHourly`). `null`
   * when the key is absent -- an API that predates it -- and that reads as
   * NOT hourly: the stub keeps its ticket layout rather than guessing.
   */
  isHourly: boolean | null;
  /** The tenant's two-letter state/province, for rows the API left NULL. */
  companyRegion: string | null;
  /** `data.overtime`'s hour split, the API's figures. */
  regularHours: number | null;
  overtimeHours: number | null;
  /**
   * The register's Overtime row (api#1169), US hourly only, null otherwise.
   * With the overtime split, hours × rate = amount, the whole overtime pay. When
   * the split declines, `rate` is null and `amount` is the stored PREMIUM.
   * Either way the groups plus this row sum to `lines.total`.
   */
  overtimeLine: { hours: number | null; rate: number | null; amount: number | null } | null;
}

function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

const CATEGORIES: PayCategory[] = ['ticket', 'shop', 'hourly_field', 'stat_holiday'];

function readGroup(g: any): PayGroup {
  const category = g.category ?? null;
  return {
    groupKey: String(g.group_key ?? g.groupKey ?? ''),
    familyKey: strOrNull(g.family_key ?? g.familyKey),
    label: strOrNull(g.group_label ?? g.groupLabel),
    category: CATEGORIES.includes(category) ? category : null,
    ticketId: strOrNull(g.ticket_id ?? g.ticketId),
    ticketNumber: strOrNull(g.ticket_number ?? g.ticketNumber),
    customerName: strOrNull(g.customer_name ?? g.customerName),
    date: strOrNull(g.date),
    amount: numOrNull(g.amount),
    entryIds: Array.isArray(g.entry_ids ?? g.entryIds) ? (g.entry_ids ?? g.entryIds).map(String) : [],
    jobNumber: strOrNull(g.job_number ?? g.jobNumber),
    jobTicketNumber: strOrNull(g.job_ticket_number ?? g.jobTicketNumber),
    hours: numOrNull(g.hours),
    rate: numOrNull(g.rate),
    rateReason: RATE_REASONS.includes(g.rate_reason ?? g.rateReason) ? (g.rate_reason ?? g.rateReason) : null,
    shifts: Array.isArray(g.shifts) ? g.shifts.map(readShift) : null,
  };
}

const RATE_REASONS: ClockRateReason[] = ['mixed_rates', 'unpriced'];

function readShift(s: any): ClockShift {
  return {
    entryId: String(s?.entry_id ?? s?.entryId ?? ''),
    timesheetId: strOrNull(s?.timesheet_id ?? s?.timesheetId),
    date: strOrNull(s?.date),
    clockInAt: strOrNull(s?.clock_in_at ?? s?.clockInAt),
    clockOutAt: strOrNull(s?.clock_out_at ?? s?.clockOutAt),
    clockInLocal: strOrNull(s?.clock_in_local ?? s?.clockInLocal),
    clockOutLocal: strOrNull(s?.clock_out_local ?? s?.clockOutLocal),
    hours: numOrNull(s?.hours),
    rate: numOrNull(s?.rate),
    amount: numOrNull(s?.amount),
  };
}

/**
 * A clock shift's in and out as the tenant's wall clock: "14:25 – 20:35", with
 * the out-day appended when the shift ended on a later day than it began
 * ("14:25 – 20:35 Oct 7"). The API's `clock_*_local` carry no date, so a shift
 * crossing midnight would otherwise read as a six-hour one beside 30 hours.
 *
 * The out-day is the tenant's day of `clockOutAt` in `timeZone`. With no
 * usable zone nothing is known about the day, so nothing is appended -- the
 * hours beside it still say how long it was.
 */
export function shiftClockText(
  s: { date: string | null; clockOutAt: string | null; clockInLocal: string | null; clockOutLocal: string | null },
  timeZone: string | null | undefined,
): string {
  const outDay = instantTenantDay(s.clockOutAt, timeZone);
  const later = outDay !== null && s.date !== null && outDay !== s.date
    ? ` ${formatCalendarDate(outDay, { month: 'short', day: 'numeric' })}`
    : '';
  return `${s.clockInLocal ?? '—'} – ${s.clockOutLocal ?? '—'}${later}`;
}

/** What a clock group's missing rate means, for the line under its figure. */
export const RATE_REASON_TEXT: Record<ClockRateReason, string> = {
  mixed_rates: 'Rates differ by shift',
  unpriced: 'A shift has no rate',
};

/**
 * "14.75 hrs × $25.00 = $368.75" -- a clock line's hours, rate and amount,
 * every figure the API's. With no single rate (mixed or unpriced) the "× rate"
 * is left out rather than averaged: "14.75 hrs = $368.75". `null` when the
 * hours are absent, so the caller keeps its own em dash.
 *
 * `money` is the caller's formatter, so the tenant's currency rides along and
 * an absent amount stays `—`.
 */
export function hoursTimesRate(
  hours: number | null,
  rate: number | null,
  amount: number | null,
  money: (v: number | null) => string,
): string | null {
  if (hours === null) return null;
  const hrs = `${fmtHours(hours)} hrs`;
  return rate !== null ? `${hrs} × ${money(rate)} = ${money(amount)}` : `${hrs} = ${money(amount)}`;
}

function readLines(l: any): PaySummaryLines | null {
  if (!l || typeof l !== 'object') return null;
  const pick = (camel: string, snake: string) => numOrNull(l[camel] ?? l[snake]);
  return {
    ticketEarnings: pick('ticketEarnings', 'ticket_earnings'),
    shopTime: pick('shopTime', 'shop_time'),
    hourlyFieldTime: pick('hourlyFieldTime', 'hourly_field_time'),
    statHoliday: pick('statHoliday', 'stat_holiday'),
    bonuses: pick('bonuses', 'bonuses'),
    mileage: pick('mileage', 'mileage'),
    expenses: pick('expenses', 'expenses'),
    overtime: pick('overtime', 'overtime'),
    shopHours: pick('shopHours', 'shop_hours'),
    shopRate: pick('shopRate', 'shop_rate'),
    hourlyFieldHours: pick('hourlyFieldHours', 'hourly_field_hours'),
    hourlyFieldRate: pick('hourlyFieldRate', 'hourly_field_rate'),
    overtimeHours: pick('overtimeHours', 'overtime_hours'),
    overtimeRate: pick('overtimeRate', 'overtime_rate'),
    other: pick('other', 'other'),
    total: pick('total', 'total'),
    residual: pick('residual', 'residual'),
  };
}

function readOvertimeLine(o: any): PayStub['overtimeLine'] {
  if (!o || typeof o !== 'object') return null;
  return { hours: numOrNull(o.hours), rate: numOrNull(o.rate), amount: numOrNull(o.amount) };
}

/** The response body (the axios `res.data`), envelope included. */
export function readPayStub(body: any): PayStub {
  const data = body && typeof body === 'object' && 'data' in body ? body.data : body;
  const rawGroups = data?.groups;
  const hourly = data?.isHourly ?? data?.is_hourly;
  const ot = data?.overtime;
  return {
    entries: Array.isArray(data?.entries) ? data.entries : [],
    groups: Array.isArray(rawGroups) ? rawGroups.map(readGroup) : null,
    lines: readLines(data?.summary?.lines),
    adjustments: Array.isArray(data?.adjustments) ? data.adjustments : [],
    isHourly: typeof hourly === 'boolean' ? hourly : null,
    companyRegion: strOrNull(data?.companyRegion ?? data?.company_region),
    regularHours: numOrNull(ot?.regularHours ?? ot?.regular_hours),
    overtimeHours: numOrNull(ot?.overtimeHours ?? ot?.overtime_hours),
    overtimeLine: readOvertimeLine(data?.overtimeLine ?? data?.overtime_line),
  };
}

/**
 * The amount an entry line shows on the stub. For a US hourly worker whose
 * overtime the API split, `stub_earnings` is the line's straight time after
 * its overtime hours moved to the Overtime row -- the stored `earnings` would
 * count those hours twice beside that row. Absent everywhere else.
 */
export function stubLineEarnings(entry: any): unknown {
  const split = entry?.stub_earnings ?? entry?.stubEarnings;
  return split !== null && split !== undefined ? split : entry?.earnings;
}

/** The line's hours after the split (`stub_hours`), or null when it did not move. */
export function stubLineHours(entry: any): number | null {
  return numOrNull(entry?.stub_hours ?? entry?.stubHours);
}

/**
 * The one value every member of a group shares, or `null` when they differ.
 * A group row shows Role, Hours and Prov only when they are the same on every
 * line under it; it never picks one line's value to stand for the rest.
 */
export function sharedValue<T>(rows: T[], read: (row: T) => string | null | undefined): string | null {
  if (rows.length === 0) return null;
  const first = read(rows[0]) || null;
  return rows.every(r => (read(r) || null) === first) ? first : null;
}
